<template>
  <div v-html="svgOutput" :style="{ 'height': size, 'width': size, 'background-color': 'white' }"></div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useTradeItemsStore, useReplacementItemsStore } from '@/stores';
// @ts-ignore
import bwipjs from 'bwip-js';
import IItemSignatureRaw from '@/models/IItemSignatureRaw';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';

const i18n = useI18n();

const unavailableSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="rgb(255 193 7)" stroke="rgb(255 193 7)"><path d="M464 256A208 208 0 1 0 48 256a208 208 0 1 0 416 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm169.8-90.7c7.9-22.3 29.1-37.3 52.8-37.3h58.3c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6c-13.3 0-24-10.7-24-24V250.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1H222.6c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/></svg>`;
const invalidSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="rgb(220, 53, 69)" stroke="rgb(220, 53, 69)"><!--!Font Awesome Pro 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.--><path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c-13.3 0-24 10.7-24 24l0 112c0 13.3 10.7 24 24 24s24-10.7 24-24l0-112c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"/></svg>`;

const replacementItemsStore = useReplacementItemsStore();
const tradeItemsStore = useTradeItemsStore();
const { data } = storeToRefs(tradeItemsStore);

const svgOutput = ref<string>(unavailableSvg);

const props = defineProps({
  itemId: { type: Number, required: true },
  itemSignature: { type: Object as () => IItemSignatureRaw, required: true },
  size: { type: String, default: '1rem' },
  renderInvalid: { type: Boolean, default: false },
});

onMounted(() => {
  svgOutput.value = toTradeItemSvgBarcode(props.itemId, props.itemSignature);
});

watch(() => [props.itemId, props.itemSignature], ([newItemId, newItemSignature]) => {
  svgOutput.value = toTradeItemSvgBarcode(newItemId as number, newItemSignature as IItemSignatureRaw);
});

const generateGS1DataMatrixBarcode = (text: string): string => {
  return (bwipjs as any).toSVG({
    bcid: 'gs1datamatrix', // Barcode type
    text: text, // Data with AI identifiers
  });
};

const generateDataMatrixBarcode = (text: string): string => {
  return (bwipjs as any).toSVG({
    bcid: 'datamatrix', // Barcode type
    text: text, // Data with AI identifiers
  });
};

const toEncodedAIs = (tradeItemCode: string, itemSignature: IItemSignatureRaw) => {
  return `${aiGtin(tradeItemCode)}${aiLot(itemSignature.lot)}${aiBestBefore(itemSignature.bestBefore)}${aiExpiry(itemSignature.expiry)}`;
}

const aiGtin = (gtin: string): string => {
  if (gtin) {
    return `(01)${gtin}`;
  }
  return '';
}

const aiLot = (lot: string | undefined): string => {
  if (lot) {
    return `(10)${lot}`;
  }
  return '';
}

const aiBestBefore = (bestBefore: string | undefined): string => {
  if (bestBefore) {
    const bestBeforeFormatted = dayjs(bestBefore).format('YYMMDD');
    return `(15)${bestBeforeFormatted}`;
  }
  return '';
}

const aiExpiry = (expiry: string | undefined): string => {
  if (expiry) {
    const expiryFormatted = dayjs(expiry).format('YYMMDD');
    return `(17)${expiryFormatted}`;
  }
  return '';
}

const toTradeItemSvgBarcode = (itemId: number, itemSignature: IItemSignatureRaw): string => {
  let baseItemId = replacementItemsStore.getBaseItem(itemId);
  if (!baseItemId) {
    baseItemId = itemId;
  }
  const tradeItem = data.value
    .filter((item) => item.itemId === baseItemId)
    .sort((a, b) => a.factor - b.factor || a.unitCode.localeCompare(b.unitCode))
    .find((() => true));
  if (!tradeItem) {
    const msg = i18n.t('error.noItemGtin', { itemId: baseItemId });
    console.info(msg);
    return `<span title="${msg}">` + unavailableSvg + "</span>";
  }
  let tradeItemCode = tradeItem.code;
  if (tradeItemCode.length < 14 && tradeItemCode.length > 0) {
    tradeItemCode = tradeItemCode.padStart(14, '0');
  }
  const encodedAIs = toEncodedAIs(tradeItemCode, itemSignature);
  try {
    return generateGS1DataMatrixBarcode(encodedAIs);
  } catch (error) {
    if (props.renderInvalid) {
      // fallback to standard Data Matrix (with trade item code only) if GS1 fails
      try {
        return generateDataMatrixBarcode(tradeItemCode);
      } catch (error) {
        if (error instanceof Error) {
          console.info(error.message);
          return `<span title="${error.message}">` + invalidSvg + "</span>";
        }
        else {
          console.info(error);
          return `<span title="${error}">` + invalidSvg + "</span>";
        }
      }
    }
    else {
      if (error instanceof Error) {
        console.info(error.message);
        return `<span title="${error.message}">` + invalidSvg + "</span>";
      }
      else {
        console.info(error);
        return `<span title="${error}">` + invalidSvg + "</span>";
      }
    }
  }
};
</script>
